import { jsPDF } from "jspdf";
import QRCodeLib from "qrcode";
import {
  formatReceiptDateTime,
  formatReceiptPrice,
  isValidTicketQrValue,
  normalizeReceiptTicket,
} from "../components/TicketReceiptCard";
import { venuePolicyToPlainText } from "./venuePolicyHtml";
import { hexToRgb } from "./sectionColor";

const FOOTER_TEXT =
  "By entering the venue, you agree to abide by the rules and regulations. Management reserves the right to refuse entry.";

const PAGE_GRAY = [239, 239, 239];
const BRAND_RED = [220, 0, 0];
const STATUS_GREEN = [5, 150, 105];
const STATUS_MUTED = [156, 163, 175];
const LABEL_GRAY = [150, 150, 150];
const LINE_GRAY = [210, 210, 210];
const SHADOW_GRAY = [210, 210, 210];
const BAR_HEIGHT = 36;

const wrapLines = (doc, text, maxWidth) => doc.splitTextToSize(String(text || ""), maxWidth);

const drawPageChrome = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFillColor(...PAGE_GRAY);
  doc.rect(0, 0, pageWidth, pageHeight, "F");
  doc.setFillColor(...BRAND_RED);
  doc.rect(0, 0, pageWidth, BAR_HEIGHT, "F");
};

const drawCardShell = (doc, { x, y, width, height }) => {
  doc.setFillColor(...SHADOW_GRAY);
  doc.roundedRect(x + 3, y + 4, width, height, 14, 14, "F");
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, width, height, 14, 14, "F");
};

const drawWrappedText = (doc, text, x, y, maxWidth, lineHeight = 11, align = "left") => {
  const lines = wrapLines(doc, text, maxWidth);
  const drawX = align === "right" ? x + maxWidth : align === "center" ? x + maxWidth / 2 : x;
  lines.forEach((line, index) => {
    doc.text(line, drawX, y + index * lineHeight, { align });
  });
  return lines.length * lineHeight;
};

const formatFieldValue = (value, preserveCase = false) => {
  const text = String(value || "-");
  return preserveCase ? text : text.toUpperCase();
};

const drawField = (doc, { label, value, x, y, width, align = "left", preserveCase = false, swatchColor = "" }) => {
  const drawX = align === "right" ? x + width : x;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...LABEL_GRAY);
  doc.text(String(label).toUpperCase(), drawX, y, { align });
  const rgb = hexToRgb(swatchColor);
  let textX = x;
  let textWidth = width;
  if (rgb) {
    const swatchSize = 6;
    const swatchY = y + 6;
    if (align === "right") {
      doc.setFillColor(rgb.r, rgb.g, rgb.b);
      doc.rect(x + width - swatchSize, swatchY, swatchSize, swatchSize, "F");
      textWidth = width - swatchSize - 4;
    } else {
      doc.setFillColor(rgb.r, rgb.g, rgb.b);
      doc.rect(x, swatchY, swatchSize, swatchSize, "F");
      textX = x + swatchSize + 4;
      textWidth = width - swatchSize - 4;
    }
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  const valueHeight = drawWrappedText(doc, formatFieldValue(value, preserveCase), textX, y + 12, textWidth, 11, align);
  return 16 + valueHeight;
};

const measureField = (doc, value, width, lineHeight = 11, preserveCase = false) => {
  const lines = wrapLines(doc, formatFieldValue(value, preserveCase), width);
  return 16 + lines.length * lineHeight;
};

const drawLogo = (doc, centerX, y) => {
  const logoWidth = 96;
  const logoHeight = 18;
  doc.setFillColor(0, 0, 0);
  doc.rect(centerX - logoWidth / 2, y - 12, logoWidth, logoHeight, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND_RED);
  doc.text("ECHO", centerX - 1, y, { align: "right" });
  doc.setTextColor(255, 255, 255);
  doc.text("JHAM", centerX + 1, y);
};

const drawPolicyBox = (doc, { x, y, width, lines, lineHeight = 11 }) => {
  const title = "VENUE POLICY GUIDELINES";
  const boxHeight = 28 + lines.length * lineHeight;
  doc.setDrawColor(...BRAND_RED);
  doc.setLineWidth(1.1);
  doc.roundedRect(x, y, width, boxHeight, 8, 8, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  const titleWidth = doc.getTextWidth(title) + 16;
  doc.setFillColor(255, 255, 255);
  doc.rect(x + width / 2 - titleWidth / 2, y - 2, titleWidth, 8, "F");
  doc.setTextColor(...BRAND_RED);
  doc.text(title, x + width / 2, y + 3, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(70, 70, 70);
  lines.forEach((line, index) => {
    doc.text(line, x + 10, y + 20 + index * lineHeight);
  });
  return boxHeight;
};

const drawFooter = (doc, { centerX, y, width }) => {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...LABEL_GRAY);
  const lines = wrapLines(doc, FOOTER_TEXT, width);
  lines.forEach((line, index) => {
    doc.text(line, centerX, y + index * 9, { align: "center" });
  });
  return lines.length * 9;
};

export const buildTicketReceiptPdf = async (ticket) => {
  const receipt = normalizeReceiptTicket(ticket);
  const policyText = venuePolicyToPlainText(receipt.policyHtml);
  const qrValue = isValidTicketQrValue(receipt.qrCode) ? receipt.qrCode : "";

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const centerX = pageWidth / 2;

  const cardX = 48;
  const cardY = 56;
  const cardWidth = pageWidth - 96;
  const innerX = cardX + 28;
  const innerWidth = cardWidth - 56;
  const colGap = 20;
  const colWidth = (innerWidth - colGap) / 2;
  const rightX = innerX + colWidth + colGap;
  const bottomMargin = 28;

  let qrDataUrl = "";
  if (qrValue) {
    try {
      qrDataUrl = await QRCodeLib.toDataURL(qrValue, { margin: 1, width: 240 });
    } catch (error) {
      console.error("QR code generation failed:", error);
    }
  }

  const qrSize = 96;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  const titleLines = wrapLines(doc, String(receipt.eventTitle).toUpperCase(), innerWidth);
  doc.setFontSize(9);
  const leftHeight =
    measureField(doc, receipt.attendeeName, colWidth, 11, true) +
    6 +
    measureField(doc, receipt.attendeeEmail, colWidth, 11, true) +
    6 +
    measureField(doc, formatReceiptDateTime(receipt.eventDate, receipt.eventTime), colWidth) +
    6 +
    measureField(doc, receipt.eventVenue, colWidth) +
    (receipt.eventAddress ? wrapLines(doc, receipt.eventAddress, colWidth).length * 10 + 8 : 0);
  const rightHeight =
    measureField(doc, receipt.tierName, colWidth) +
    6 +
    measureField(doc, receipt.sectionDisplay || "-", colWidth) +
    6 +
    measureField(doc, receipt.seatLabel || "Assigned at issuance", colWidth) +
    6 +
    measureField(doc, formatReceiptPrice(receipt.price), colWidth);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const policyLines = policyText ? wrapLines(doc, policyText, innerWidth - 20) : [];
  const policyBoxHeight = policyLines.length ? 28 + policyLines.length * 11 : 0;
  const footerHeight = wrapLines(doc, FOOTER_TEXT, innerWidth).length * 9;

  const headerHeight = 28 + 22 + 18 + (qrValue ? qrSize + 14 + 12 + 13 + 16 : 28) + 22;
  const detailsHeight = titleLines.length * 18 + 16 + Math.max(leftHeight, rightHeight) + 22;
  const page1PolicyAndFooter = policyBoxHeight
    ? 22 + policyBoxHeight + 16 + footerHeight + 24
    : 16 + footerHeight + 24;
  const page1Needed = headerHeight + detailsHeight + page1PolicyAndFooter + 20;
  const maxCardHeight = pageHeight - cardY - bottomMargin;
  const policyFitsOnFirstPage = page1Needed <= maxCardHeight;

  const page1CardHeight = Math.min(
    maxCardHeight,
    policyFitsOnFirstPage ? page1Needed : headerHeight + detailsHeight + 36
  );

  const drawHeaderAndDetails = (startY) => {
    let currentY = startY;
    drawLogo(doc, centerX, currentY);
    currentY += 22;

    const used = /^(used|expired)$/i.test(receipt.statusLabel);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...(used ? STATUS_MUTED : STATUS_GREEN));
    doc.text(String(receipt.statusLabel).toUpperCase(), centerX, currentY, { align: "center" });
    currentY += 18;

    if (qrValue && qrDataUrl) {
      doc.addImage(qrDataUrl, "PNG", centerX - qrSize / 2, currentY, qrSize, qrSize);
      currentY += qrSize + 12;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...LABEL_GRAY);
      doc.text("Manual entry if QR fails", centerX, currentY, { align: "center" });
      currentY += 14;
      doc.text("TICKET ID", centerX, currentY, { align: "center" });
      currentY += 13;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(receipt.manualTicketId, centerX, currentY, { align: "center" });
      currentY += 18;
    } else {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...BRAND_RED);
      doc.text("Ticket invalid, please contact support.", centerX, currentY, { align: "center" });
      currentY += 22;
    }

    doc.setDrawColor(...LINE_GRAY);
    doc.setLineWidth(0.8);
    doc.setLineDash([]);
    doc.line(innerX, currentY, innerX + innerWidth, currentY);
    currentY += 22;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    currentY += drawWrappedText(doc, String(receipt.eventTitle).toUpperCase(), innerX, currentY, innerWidth, 18, "center");
    currentY += 16;

    const leftColTop = currentY;
    let leftY = currentY;
    leftY += drawField(doc, {
      label: "Name",
      value: receipt.attendeeName,
      x: innerX,
      y: leftY,
      width: colWidth,
      preserveCase: true,
    });
    leftY += 6;
    leftY += drawField(doc, {
      label: "Email",
      value: receipt.attendeeEmail,
      x: innerX,
      y: leftY,
      width: colWidth,
      preserveCase: true,
    });
    leftY += 6;
    leftY += drawField(doc, {
      label: "Date",
      value: formatReceiptDateTime(receipt.eventDate, receipt.eventTime),
      x: innerX,
      y: leftY,
      width: colWidth,
    });
    leftY += 6;
    leftY += drawField(doc, {
      label: "Venue",
      value: receipt.eventVenue,
      x: innerX,
      y: leftY,
      width: colWidth,
    });
    if (receipt.eventAddress) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...LABEL_GRAY);
      leftY += drawWrappedText(doc, receipt.eventAddress, innerX, leftY, colWidth, 10, "left") + 8;
    }

    let rightY = leftColTop;
    rightY += drawField(doc, {
      label: "Ticket Type",
      value: receipt.tierName,
      x: rightX,
      y: rightY,
      width: colWidth,
      align: "right",
    });
    rightY += 6;
    rightY += drawField(doc, {
      label: "Section",
      value: receipt.sectionDisplay || "-",
      x: rightX,
      y: rightY,
      width: colWidth,
      align: "right",
      swatchColor: receipt.sectionColor,
    });
    rightY += 6;
    rightY += drawField(doc, {
      label: "Seat",
      value: receipt.seatLabel || "Assigned at issuance",
      x: rightX,
      y: rightY,
      width: colWidth,
      align: "right",
    });
    rightY += 6;
    rightY += drawField(doc, {
      label: "Price",
      value: formatReceiptPrice(receipt.price),
      x: rightX,
      y: rightY,
      width: colWidth,
      align: "right",
    });

    currentY = Math.max(leftY, rightY) + 14;
    doc.setDrawColor(...LINE_GRAY);
    doc.setLineWidth(0.8);
    doc.setLineDash([3, 3], 0);
    doc.line(innerX, currentY, innerX + innerWidth, currentY);
    doc.setLineDash([]);
    return currentY + 22;
  };

  drawPageChrome(doc);
  drawCardShell(doc, { x: cardX, y: cardY, width: cardWidth, height: page1CardHeight });
  let currentY = drawHeaderAndDetails(cardY + 28);

  if (policyFitsOnFirstPage) {
    if (policyLines.length) {
      currentY += drawPolicyBox(doc, {
        x: innerX,
        y: currentY,
        width: innerWidth,
        lines: policyLines,
      });
      currentY += 16;
    }
    drawFooter(doc, { centerX, y: currentY, width: innerWidth });
    return doc;
  }

  doc.addPage();
  drawPageChrome(doc);
  const page2CardHeight = 40 + (policyLines.length ? policyBoxHeight + 16 : 0) + footerHeight + 24;
  drawCardShell(doc, { x: cardX, y: cardY, width: cardWidth, height: Math.min(page2CardHeight, maxCardHeight) });
  currentY = cardY + 28;
  if (policyLines.length) {
    currentY += drawPolicyBox(doc, {
      x: innerX,
      y: currentY,
      width: innerWidth,
      lines: policyLines,
    });
    currentY += 16;
  }
  drawFooter(doc, { centerX, y: currentY, width: innerWidth });
  return doc;
};
